/** Hand-rolled types for the public payer endpoints, until the backend exposes them in its OpenAPI doc. */

export type InvoiceStatus =
  | "Draft"
  | "Issued"
  | "Open"
  | "Paid"
  | "Void"
  | "PartiallyPaid"
  | "Credited";

export interface PublicInvoiceResponse {
  documentNumber: string;
  currency: string;
  totalAmount: number;
  amountPaid: number;
  // Total of issued credit notes applied to this invoice. amountDue is already net of it.
  amountCredited: number;
  amountDue: number;
  issueDate: string;
  dueDate: string | null;
  status: InvoiceStatus;
  memo: string | null;
  customerName: string;
  organisationName: string;
  organisationLogoUrl: string | null;
  organisationPrimaryColour: string | null;
  // True when the issuing org's Client has a connected WhatsApp number, so the payer can opt in.
  whatsAppOptInAvailable: boolean;
  // True when this invoice's customer already consented to WhatsApp messages.
  whatsAppOptedIn: boolean;
  // The early payment discount on offer today; null when there is none or it can no longer be taken (expired, or
  // something already paid or credited). The checkout charges amountDueNow while it is present.
  earlyPaymentDiscount?: PublicEarlyPaymentDiscount | null;
  // A demo organisation's invoice: pay it on the demo card form, never Stripe.
  isDemo?: boolean;
}

export interface PublicEarlyPaymentDiscount {
  // Off amountDue, if paid by expiresOn.
  amount: number;
  // yyyy-MM-dd, the last day to pay at the discount.
  expiresOn: string;
  // amountDue - amount: what the payer is charged if they pay now.
  amountDueNow: number;
}

/** What the payer is asked for right now: the discounted amount while an early payment discount is live. */
export function amountDueNow(invoice: PublicInvoiceResponse): number {
  return invoice.earlyPaymentDiscount?.amountDueNow ?? invoice.amountDue;
}

export interface CheckoutSessionResponse {
  clientSecret: string;
  publishableKey: string;
  connectedAccountId: string;
  amount: number;
  currency: string;
}

export interface ProblemDetails {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  // The backend's stable error code (Error.ToProblemResult puts it in `code` and `title`).
  code?: string;
  // RFC 9110 validation problems: messages keyed by field.
  errors?: Record<string, string[]>;
}

export type PublicApiError =
  | { kind: "invalid-token"; problem: ProblemDetails }       // 404 Payment.InvalidToken
  | { kind: "not-payable"; problem: ProblemDetails }         // 409 Payment.InvoiceNotPayable
  | { kind: "account-disabled"; problem: ProblemDetails }    // 409 PaymentAccount.NotEnabled
  | { kind: "provider-error"; problem: ProblemDetails }      // 502 Payment.ProviderError
  | { kind: "network"; cause: unknown }
  | { kind: "unknown"; status: number; problem: ProblemDetails };

async function parseProblem(res: Response): Promise<ProblemDetails> {
  try {
    return (await res.json()) as ProblemDetails;
  } catch {
    return { status: res.status };
  }
}

function classify(res: Response, problem: ProblemDetails): PublicApiError {
  // The stable code is in `code` (and `title`); `type` is the RFC 9110 section URL.
  const code = problem.code ?? problem.title;
  if (res.status === 404 && code === "Payment.InvalidToken") {
    return { kind: "invalid-token", problem };
  }
  if (res.status === 409 && code === "Payment.InvoiceNotPayable") {
    return { kind: "not-payable", problem };
  }
  if (res.status === 409 && code === "PaymentAccount.NotEnabled") {
    return { kind: "account-disabled", problem };
  }
  if (res.status === 502 && code === "Payment.ProviderError") {
    return { kind: "provider-error", problem };
  }
  return { kind: "unknown", status: res.status, problem };
}

export async function fetchInvoice(token: string): Promise<
  { ok: true; data: PublicInvoiceResponse } | { ok: false; error: PublicApiError }
> {
  try {
    const res = await fetch(`/api/proxy/v1/i/${encodeURIComponent(token)}`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      const problem = await parseProblem(res);
      return { ok: false, error: classify(res, problem) };
    }
    const data = (await res.json()) as PublicInvoiceResponse;
    return { ok: true, data };
  } catch (cause) {
    return { ok: false, error: { kind: "network", cause } };
  }
}

export type BillState =
  | "Created"
  | "Extracting"
  | "OcrProcessing" // obsolete alias, retained for in-flight bills during the rename drain
  | "PendingReview"
  | "NeedsApproval"
  | "Approved"
  | "Rejected"
  | "Scheduled"
  | "Paid"
  | "ExtractionFailed"
  | "OcrFailed"; // obsolete alias, retained for in-flight bills during the rename drain

export interface PublicBillResponse {
  billId: string;
  state: BillState;
  currency: string;
  subTotal: number;
  taxAmount: number;
  totalAmount: number;
  invoiceDate: string | null;
  dueDate: string | null;
  invoiceNumber: string | null;
  supplierName: string | null;
  paymentTerms: string | null;
  approvedAt: string | null;
  approvedByEmail: string | null;
  rejectedAt: string | null;
  rejectedByEmail: string | null;
  rejectionReason: string | null;
  paymentDate: string | null;
  paidAt: string | null;
  pdfSignedUrl: string | null;
  clientLogoUrl: string | null;
  clientPrimaryColour: string | null;
}

export type BillApprovalError =
  | { kind: "invalid-token"; problem: ProblemDetails }
  | { kind: "not-authorised"; problem: ProblemDetails }
  | { kind: "reason-required"; problem: ProblemDetails }
  | { kind: "network"; cause: unknown }
  | { kind: "unknown"; status: number; problem: ProblemDetails };

function classifyBillError(res: Response, problem: ProblemDetails): BillApprovalError {
  const code = problem.code ?? problem.title;
  if (res.status === 404 && code === "Bill.InvalidApprovalToken")
    return { kind: "invalid-token", problem };
  if (res.status === 403 && code === "Bill.ReviewerNoLongerAuthorised")
    return { kind: "not-authorised", problem };
  if (res.status === 400 && code === "Bill.RejectionReasonRequired")
    return { kind: "reason-required", problem };
  return { kind: "unknown", status: res.status, problem };
}

export async function fetchBill(token: string): Promise<
  { ok: true; data: PublicBillResponse } | { ok: false; error: BillApprovalError }
> {
  try {
    const res = await fetch(`/api/proxy/v1/b/${encodeURIComponent(token)}`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      const problem = await parseProblem(res);
      return { ok: false, error: classifyBillError(res, problem) };
    }
    const data = (await res.json()) as PublicBillResponse;
    return { ok: true, data };
  } catch (cause) {
    return { ok: false, error: { kind: "network", cause } };
  }
}

export async function approveBill(token: string): Promise<
  { ok: true; data: PublicBillResponse } | { ok: false; error: BillApprovalError }
> {
  try {
    const res = await fetch(`/api/proxy/v1/b/${encodeURIComponent(token)}/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
    if (!res.ok) {
      const problem = await parseProblem(res);
      return { ok: false, error: classifyBillError(res, problem) };
    }
    const data = (await res.json()) as PublicBillResponse;
    return { ok: true, data };
  } catch (cause) {
    return { ok: false, error: { kind: "network", cause } };
  }
}

export async function rejectBill(token: string, reason: string): Promise<
  { ok: true; data: PublicBillResponse } | { ok: false; error: BillApprovalError }
> {
  try {
    const res = await fetch(`/api/proxy/v1/b/${encodeURIComponent(token)}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) {
      const problem = await parseProblem(res);
      return { ok: false, error: classifyBillError(res, problem) };
    }
    const data = (await res.json()) as PublicBillResponse;
    return { ok: true, data };
  } catch (cause) {
    return { ok: false, error: { kind: "network", cause } };
  }
}

export async function createCheckoutSession(token: string): Promise<
  { ok: true; data: CheckoutSessionResponse } | { ok: false; error: PublicApiError }
> {
  try {
    const res = await fetch(`/api/proxy/v1/i/${encodeURIComponent(token)}/checkout-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
    if (!res.ok) {
      const problem = await parseProblem(res);
      return { ok: false, error: classify(res, problem) };
    }
    const data = (await res.json()) as CheckoutSessionResponse;
    return { ok: true, data };
  } catch (cause) {
    return { ok: false, error: { kind: "network", cause } };
  }
}

export type WhatsAppConsentError =
  | { kind: "invalid-phone"; message: string } // 400 Customer.InvalidWhatsAppPhone
  | { kind: "unavailable" }                    // 404 WhatsApp.OptInUnavailable (or the token went stale)
  | { kind: "network"; cause: unknown }
  | { kind: "unknown"; status: number; problem: ProblemDetails };

export async function submitWhatsAppConsent(
  token: string,
  phone: string,
): Promise<{ ok: true } | { ok: false; error: WhatsAppConsentError }> {
  try {
    const res = await fetch(`/api/proxy/v1/i/${encodeURIComponent(token)}/whatsapp-consent`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ phone, consent: true }),
    });
    if (res.ok) return { ok: true };
    const problem = await parseProblem(res);
    if (res.status === 404) return { ok: false, error: { kind: "unavailable" } };
    if (res.status === 400) {
      // A coded error carries its message in `detail`; a validation problem carries it under `errors`.
      const message = problem.detail ?? Object.values(problem.errors ?? {}).flat()[0];
      if (message) return { ok: false, error: { kind: "invalid-phone", message } };
    }
    return { ok: false, error: { kind: "unknown", status: res.status, problem } };
  } catch (cause) {
    return { ok: false, error: { kind: "network", cause } };
  }
}

// An SMB pays an approved AP bill to its supplier by open banking. Hidden from OpenAPI, like /i and /b.

export type BillPaymentLinkState =
  | "Approved"
  | "Scheduled"
  | "PaymentInProgress"
  | "PaymentFailed"
  | "Paid"
  | "Voided"
  | (string & {}); // the link can outlive these states (e.g. a reopened bill), so stay open

export type BillPaymentAttemptStatus =
  | "Initiating"
  | "AwaitingAuthorisation"
  | "Authorised"
  | "Pending"
  | "Completed"
  | "Failed"
  | "Expired";

// Yapily Connect's regulatory disclosure. Render text and both links verbatim on the consent step.
export interface YapilyDisclosure {
  text: string;
  termsUrl: string;
  privacyUrl: string;
}

export interface PublicBillPaymentResponse {
  organisationName: string;
  clientLogoUrl: string | null;
  clientPrimaryColour: string | null;
  supplierName: string | null;
  invoiceNumber: string | null;
  dueDate: string | null;
  currency: string;
  totalAmount: number;
  outstanding: number;
  state: BillPaymentLinkState;
  payeeName: string | null;
  payeeAccountNumberLast4: string | null;
  // An error code (BankPayment.* or Bill.TransitionFailed) when pay-by-bank can't start now.
  unavailableReason: string | null;
  // The backend's own sentence for that code. Written for the organisation's admins, so the page maps codes itself.
  unavailableMessage: string | null;
  latestPayment: {
    status: BillPaymentAttemptStatus;
    errorMessage: string | null;
    createdAt: string;
  } | null;
  disclosure: YapilyDisclosure;
  // A demo organisation's bill: show the demo note, not the Yapily disclosure.
  isDemo?: boolean;
}

export interface PaymentInstitution {
  id: string;
  name: string;
  fullName: string | null;
  countries: string[];
  iconUrl: string | null;
}

export interface PaymentInstitutionsResponse {
  institutions: PaymentInstitution[];
  disclosure: YapilyDisclosure;
}

export interface InitiateBillBankPaymentResponse {
  billPaymentId: string;
  authorisationUrl: string;
  expiresAt: string;
  disclosure: YapilyDisclosure;
}

export type BillPaymentLinkError =
  | { kind: "invalid-token"; problem: ProblemDetails } // 404 Bill.InvalidPaymentLink
  | { kind: "network"; cause: unknown }
  | { kind: "unknown"; status: number; problem: ProblemDetails };

async function billPaymentLinkRequest<T>(
  path: string,
  init?: RequestInit,
): Promise<{ ok: true; data: T } | { ok: false; error: BillPaymentLinkError }> {
  try {
    const res = await fetch(path, {
      ...init,
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
    if (!res.ok) {
      const problem = await parseProblem(res);
      const code = problem.code ?? problem.title;
      if (res.status === 404 && code === "Bill.InvalidPaymentLink")
        return { ok: false, error: { kind: "invalid-token", problem } };
      return { ok: false, error: { kind: "unknown", status: res.status, problem } };
    }
    return { ok: true, data: (await res.json()) as T };
  } catch (cause) {
    return { ok: false, error: { kind: "network", cause } };
  }
}

export function fetchBillPayment(token: string) {
  return billPaymentLinkRequest<PublicBillPaymentResponse>(`/api/proxy/v1/p/${encodeURIComponent(token)}`);
}

export function fetchBillPaymentInstitutions(token: string) {
  return billPaymentLinkRequest<PaymentInstitutionsResponse>(
    `/api/proxy/v1/p/${encodeURIComponent(token)}/institutions`,
  );
}

export function payBillByBank(token: string, institutionId: string) {
  return billPaymentLinkRequest<InitiateBillBankPaymentResponse>(
    `/api/proxy/v1/p/${encodeURIComponent(token)}/pay/bank`,
    { method: "POST", body: JSON.stringify({ institutionId }) },
  );
}

// Hand-written: POST /i/{token}/demo-card-payment is demo scaffolding, hidden from every OpenAPI document.
export type DemoPayoutSpeed = "Standard" | "Instant";

export interface DemoCardPaymentResponse {
  paymentId: string;
  amount: number;
  currency: string;
  payoutSpeed: DemoPayoutSpeed;
}

export async function createDemoCardPayment(
  token: string,
  amount: number,
  payoutSpeed: DemoPayoutSpeed,
): Promise<{ ok: true; data: DemoCardPaymentResponse } | { ok: false; error: PublicApiError }> {
  try {
    const res = await fetch(`/api/proxy/v1/i/${encodeURIComponent(token)}/demo-card-payment`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ amount, payoutSpeed }),
    });
    if (!res.ok) {
      const problem = await parseProblem(res);
      return { ok: false, error: classify(res, problem) };
    }
    return { ok: true, data: (await res.json()) as DemoCardPaymentResponse };
  } catch (cause) {
    return { ok: false, error: { kind: "network", cause } };
  }
}
